'use client';

import {
  Input,
  MultiSelect,
  NumberInput,
  Radio,
  Select,
  TagsInput,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { Button, Dropzone } from '@scaleup/frontend/ui';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCalendar,
  IconCheck,
  IconCoinFilled,
  IconEdit,
  IconPackages,
  IconPencil,
  IconPencilCheck,
  IconTrophyFilled,
  IconUpload,
  IconUserStar,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  AwaitedReactNode,
  JSXElementConstructor,
  Key,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  ReactPortal,
  useState,
} from 'react';
import { FormSection, IntakeBase } from './common';
import { useIntakeUpload, useSupplierIntakeContext } from './context';
import { Award, Supplier, SupplierData } from '@scaleup/models';
import { set } from 'lodash';

const IconWrapper = ({ children }: PropsWithChildren) => (
  <div className="rounded-full h-[2.5rem] w-[2.5rem] flex items-center justify-center bg-primary-50">
    {children}
  </div>
);

export const Structure = () => {
  const {
    setCurrentStep,
    structureForm,
    supplier,
    updateSupplier,
    lastStep,
    setLastStep,
  } = useSupplierIntakeContext();
  const [awards, setAwards] = useState<Award[]>(
    supplier.supplierData.awards || []
  );
  const [awardsDirty, setAwardsDirty] = useState<boolean>(false);
  const { intakeFileUpload, isLoading } = useIntakeUpload();
  const [loadingStructure, setLoadingStructure] = useState<boolean>(false);

  const uploadFile = async (file: File) => {
    await intakeFileUpload(file, (key) => {
      structureForm.setFieldValue('capabilityStatementFileKey', key);
    });
  };

  const handleClick = async () => {
    // add api call

    const { hasErrors } = structureForm.validate();
    if (hasErrors) {
      return;
    }

    if (!structureForm.isDirty() && !awardsDirty) {
      if (lastStep === 'review') {
        setLastStep('structure');
        setCurrentStep('review');
      } else {
        setLastStep('structure');
        setCurrentStep('naics');
      }
      return;
    }
    const {
      annualRevenue1,
      annualRevenue2,
      annualRevenue3,
      capabilityStatementFileKey,
      ...restValues
    } = structureForm.values;
    const supplierData: SupplierData = {
      ...supplier.supplierData,
      ...restValues,
      awards: awards,
      capabilitySheet: {
        fileKey: capabilityStatementFileKey,
      },
      yearlyTotalRevenue: [
        {
          year: dayjs().subtract(1, 'year').year(),
          revenue: annualRevenue1,
        },
        {
          year: dayjs().subtract(2, 'year').year(),
          revenue: annualRevenue2,
        },
        {
          year: dayjs().subtract(3, 'year').year(),
          revenue: annualRevenue3,
        },
      ],
    };
    setLoadingStructure(true);
    updateSupplier({
      ...supplier,
      supplierData: supplierData,
    }).then((newSupplier: Supplier) => {
      setLoadingStructure(false);
      if (lastStep === 'review') {
        setLastStep('structure');
        setCurrentStep('review');
      } else {
        setLastStep('structure');
        setCurrentStep('naics');
      }
      console.log('Updated Supplier:', newSupplier);
    });
  };

  return (
    <IntakeBase currentStep={3}>
      <div className="flex flex-col gap-2">
        <p className="text-text-primary font-bold text-xl">Company Structure</p>

        <p className="text-text-secondary">
          Tell us about your industry focus, target markets, and the sectors you
          serve.
        </p>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <FormSection
          withDivider
          icon={
            <IconWrapper>
              <IconUserStar color="#4fb3cf" />
            </IconWrapper>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-text-primary font-bold text-xl pt-2">
              Leadership & Organizational Structure
            </p>

            <div className="w-full flex gap-4">
              <div className="w-1/3">
                <Select
                  label="Number of Employees"
                  required
                  data={[
                    'Self-employed',
                    '1 - 5 employees',
                    '6 - 9 employees',
                    '10 - 19 employees',
                    '20 - 50 employees',
                    '51 - 99 employees',
                    '100 - 249 employees',
                    '250+ employees',
                  ]}
                  classNames={{ input: 'w-full', wrapper: 'w-full' }}
                  error={structureForm.errors.numberOfEmployees}
                  {...structureForm.getInputProps('numberOfEmployees')}
                  //Toggle the dirty state when the input changes, but let event propogate
                />
              </div>

              <div className="w-1/3">
                <Select
                  label="Number of Contractors"
                  required
                  data={[
                    'None',
                    '1 - 5 contractors',
                    '6 - 9 contractors',
                    '10 - 19 contractors',
                    '20 - 50 contractors',
                    '51 - 99 contractors',
                    '100 - 249 contractors',
                    '250+ contractors',
                  ]}
                  classNames={{ input: 'w-full', wrapper: 'w-full' }}
                  error={structureForm.errors.numberOfContractors}
                  {...structureForm.getInputProps('numberOfContractors')}
                />
              </div>

              <div className="w-1/3">
                <Radio.Group
                  label="Does the company have a Board of Directors/Advisors"
                  required
                  name="boardOfAdvisors"
                  {...structureForm.getInputProps('boardOfAdvisors')}
                  error={structureForm.errors.boardOfAdvisors}
                >
                  <div className="flex gap-8">
                    <Radio value="true" label="Yes" />
                    <Radio value="false" label="No" />
                  </div>
                </Radio.Group>
              </div>
            </div>
            {structureForm.getValues().boardOfAdvisors === 'true' && (
              <div className="w-full flex gap-4">
                <div className="w-1/2">
                  <MultiSelect
                    label="Please select your expectations of board directors/advisors"
                    required={
                      structureForm.getValues().boardOfAdvisors === 'true'
                    }
                    data={[
                      'Actively advocate with potential customers on behalf of the company',
                      'Assist in capital raising matters',
                      'Coach executives on leadership matters',
                      'Invest their own money',
                      'Make connections to potential customers that result in net new revenues',
                      'Offer strategies and ideas tp implement in the business',
                      'Participate in customer meetings',
                      'Provide functional or technical insights',
                      'Provide industry-specific insights',
                    ]}
                    classNames={{ input: 'w-full', wrapper: 'w-full' }}
                    error={structureForm.errors.bOAExpectations}
                    {...structureForm.getInputProps('bOAExpectations')}
                  />
                </div>
              </div>
            )}
            <div className="w-full flex gap-4">
              <div className="w-1/2">
                <NumberInput
                  label="Ethnic Minority Percentage"
                  required
                  suffix="%"
                  allowDecimal={false}
                  min={0}
                  max={100}
                  classNames={{ input: 'w-full', wrapper: 'w-full' }}
                  error={structureForm.errors.ethnicMinorityPercentage}
                  {...structureForm.getInputProps('ethnicMinorityPercentage')}
                />
              </div>

              <div className="w-1/2">
                <NumberInput
                  label="Women Employee Percentage"
                  required
                  suffix="%"
                  allowDecimal={false}
                  min={0}
                  max={100}
                  classNames={{ input: 'w-full', wrapper: 'w-full' }}
                  error={structureForm.errors.womenEmployeePercentage}
                  {...structureForm.getInputProps('womenEmployeePercentage')}
                />
              </div>
            </div>

            <div className="w-full">
              <TagsInput
                label="We have the following executive leadership in place today. (Select all that apply, and add any additional roles not listed.)"
                required
                data={[
                  'CEO/President',
                  'CFO',
                  'COO/Head of Operations',
                  'CTO',
                  'CIO',
                  'CHRO/Head of HR',
                  'CMO/Head of Marketing',
                  'CISO',
                  'CPO',
                ]}
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.executiveLeadershipRoles}
                {...structureForm.getInputProps('executiveLeadershipRoles')}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          withDivider
          icon={
            <IconWrapper>
              <IconCoinFilled color="#4fb3cf" />
            </IconWrapper>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-text-primary font-bold text-xl pt-2">
              Financial Structure
            </p>
            <div className="w-full">
              <Select
                label="I/we would rather own 100% of a $30M company than 60% of a $100M company."
                data={[
                  'Disagree',
                  'Somewhat Disagree',
                  'Somewhat Agree',
                  'Agree',
                ]}
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.ownershipQuestion}
                {...structureForm.getInputProps('ownershipQuestion')}
              />
            </div>
            <div className="w-full">
              <TagsInput
                data={[
                  'Bank Term Loan',
                  'Convertible debt',
                  'Factoring',
                  'Friends and Family',
                  'Line of Credit',
                  'No outside capital',
                  'Other Loans',
                  'Private Credit',
                  'Private Equity',
                  'Venture Capital',
                  'Angel Investment',
                  'Grants',
                  'Crowdfunding',
                ]}
                label="We have used the following types of financing in the past two years. (Check all that apply, or add any additional types not listed.)"
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.financingUsed}
                {...structureForm.getInputProps('financingUsed')}
              />
            </div>
            <div className="w-full">
              <Select
                data={[
                  '<6%',
                  '6% - 7.9%',
                  '8% - 9.9%',
                  '10% - 11.9%',
                  '12% - 13.9%',
                  '14% - 15.9%',
                  '16% - 19.9%',
                  '20% - 29.9%',
                  '30%+',
                  'Do not know',
                  'No outside financing used',
                ]}
                label="If you are using or have used outside financing (e.g., factoring, bank, friends and family, equity), how much does/did it cost you on average annually in percentages?"
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.costOfFinancing}
                {...structureForm.getInputProps('costOfFinancing')}
              />
            </div>

            <div className="w-full">
              <TagsInput
                label="Please select how you would use outside capital if you had access to it. (Select all that apply, and add any additional uses not listed.)"
                required
                data={[
                  'Build inventory',
                  'Buy another company',
                  'Buy new equipment',
                  'Finance receivables',
                  'Hire operations people',
                  'Hire sales people',
                  'Purchase property',
                  'Refinance/distribute to owner(s)',
                ]}
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.useForOutsideCapital}
                {...structureForm.getInputProps('useForOutsideCapital')}
              />
            </div>

            <div className="w-1/2">
              <Select
                label="Current Revenue Range"
                required
                data={[
                  'Less than $1 million',
                  '$1 million - $2.9 million',
                  '$3 million - $4.9 million',
                  '$5 million - $9.9 million',
                  '$10 million - $14.9 million',
                  '$15 million - $19.9 million',
                  '$20 million - $29.9 million',
                  '$30 million - $39.9 million',
                  '$40 million - $49.9 million',
                  '$50 million or more',
                ]}
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.currentRevenueRange}
                {...structureForm.getInputProps('currentRevenueRange')}
              />
            </div>

            <div>
              <p className="text-text-primary font-bold">Annual Revenue</p>
              <p className="text-text-primary">
                Please provide your total annual revenue for the last 3 years.
              </p>
            </div>

            <div className="w-1/4">
              <NumberInput
                label={`Total Revenue for ${dayjs().subtract(1, 'year').year()}`}
                required
                prefix="$"
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.annualRevenue1}
                {...structureForm.getInputProps('annualRevenue1')}
              />
            </div>

            <div className="w-1/4">
              <NumberInput
                label={`Total Revenue for ${dayjs().subtract(2, 'year').year()}`}
                required
                prefix="$"
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.annualRevenue2}
                {...structureForm.getInputProps('annualRevenue2')}
              />
            </div>

            <div className="w-1/4">
              <NumberInput
                prefix="$"
                label={`Total Revenue for ${dayjs().subtract(3, 'year').year()}`}
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.annualRevenue3}
                {...structureForm.getInputProps('annualRevenue3')}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          withDivider
          icon={
            <IconWrapper>
              <IconPackages color="#4fb3cf" />
            </IconWrapper>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-text-primary font-bold text-xl pt-2">
              Service Offerings
            </p>

            <div className="w-full">
              <MultiSelect
                label="Digital Growth Needs"
                description="What are the top three digital products your company needs to grow?"
                data={[
                  'Accounting and financial',
                  'Behavioral/product analytics',
                  'Business intelligence',
                  'Content management',
                  'Customer relationship management (CRM)',
                  'Data storage and querying',
                  'Enterprise resource planning (ERP)',
                  'HR and talent management',
                  'Marketing analytics',
                  'Servers and load balancing',
                ]}
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.digitalProducts}
                {...structureForm.getInputProps('digitalProducts')}
              />
            </div>

            <div className="w-full">
              <TagsInput
                label="Outsourced Services"
                description="What are the top three third-party/outsourced services your company needs to grow?"
                data={[
                  'Accounting/finance',
                  'Business development/sales',
                  'Customer support',
                  'Information technology',
                  'Investment banking',
                  'Legal',
                  'Logistics',
                  'Management Consulting',
                  'Marketing',
                ]}
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.outsourcedServices}
                {...structureForm.getInputProps('outsourcedServices')}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          withDivider
          icon={
            <IconWrapper>
              <IconTrophyFilled color="#4fb3cf" />
            </IconWrapper>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-text-primary font-bold text-xl pt-2">
              Business Credentials
            </p>

            <div className="w-full flex flex-col gap-2">
              <p className="text-text-primary">
                <strong>Capability Statement</strong> (A document highlighting
                the company's expertise, accomplishments, and unique value
                proposition.)
              </p>
              {!structureForm.getValues().capabilityStatementFileKey && (
                <Dropzone
                  disabled={Boolean(
                    structureForm.getValues().capabilityStatementFileKey
                  )}
                  isLoading={isLoading}
                  displayInstructions
                  handleUpload={uploadFile}
                />
              )}
              {structureForm.getValues().capabilityStatementFileKey && (
                <div className="flex flex-cols items-center ">
                  <p className="text-text-primary">
                    {
                      structureForm
                        .getValues()
                        .capabilityStatementFileKey.split('/')[1]
                    }
                  </p>
                  <UnstyledButton
                    className="text-red-500 p-3"
                    onClick={() => {
                      structureForm.setFieldValue(
                        'capabilityStatementFileKey',
                        undefined
                      );
                    }}
                  >
                    <IconX />
                  </UnstyledButton>
                </div>
              )}
            </div>
            <div className="w-full">
              <h3 className="text-text-primary font-bold">
                Awards & Recognitions
              </h3>
              {awards.map((award: Award, index: number) => (
                <div key={index} className="flex w-auto p-2 mt-3 rounded">
                  <IconTrophyFilled color="#4fb3cf" />
                  <TextInput
                    label="Award"
                    className="w-1/2 mr-3"
                    value={award.name}
                    onChange={(value) => {
                      const temp: Award[] = JSON.parse(JSON.stringify(awards));
                      if (!temp[index]) {
                        temp[index] = { name: '', year: '' };
                      }
                      temp[index].name = value.currentTarget.value;
                      setAwards(temp);
                      setAwardsDirty(true);
                    }}
                  />
                  <TextInput
                    label="Year"
                    value={award.year}
                    onChange={(value) => {
                      const temp: Award[] = JSON.parse(JSON.stringify(awards));
                      if (!temp[index]) {
                        temp[index] = { name: '', year: '' };
                      }
                      temp[index].year = value.currentTarget.value;
                      setAwards(temp);
                      setAwardsDirty(true);
                    }}
                  />
                  <UnstyledButton
                    className="flex text-red-500 pl-3 pr-3 items-end justify-end h-auto"
                    onClick={() => {
                      const temp: Award[] = JSON.parse(JSON.stringify(awards));
                      temp.splice(index, 1);
                      setAwards(temp);
                      setAwardsDirty(true);
                    }}
                  >
                    <IconX className="h-auto w-auto" />
                  </UnstyledButton>
                </div>
              ))}
              <UnstyledButton
                className="text-blue-600"
                onClick={() => {
                  const temp: Award[] = JSON.parse(JSON.stringify(awards));
                  temp.push({ name: '', year: '' });
                  setAwards(temp);
                }}
              >
                + Add New Award
              </UnstyledButton>
            </div>
            <div className="w-full">
              <TagsInput
                label="Past Clients"
                placeholder="Please share any notable clients or companies the business has served."
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={structureForm.errors.previousCustomers}
                {...structureForm.getInputProps('previousCustomers')}
              />
            </div>
          </div>
        </FormSection>
      </div>

      <div className="flex justify-between">
        <Button
          text="Back"
          variant="secondary"
          prefixIcon={<IconArrowLeft />}
          onClick={() => {
            if (lastStep === 'review') {
              setLastStep('structure');
              setCurrentStep('review');
            } else {
              setLastStep('structure');
              setCurrentStep('industry');
            }
          }}
        />

        <Button
          text="Continue"
          variant="primary"
          suffixIcon={<IconArrowRight />}
          onClick={handleClick}
          loading={loadingStructure}
        />
      </div>
    </IntakeBase>
  );
};
